import React from 'react'
import { useTheme } from 'styled-components'

import { ReactComponent as FrameIcon } from '@sketch/icons/frame-17'
import { ReactComponent as TextIcon } from '@sketch/icons/textformat-17'
import { ReactComponent as ShapeIcon } from '@sketch/icons/squares-2-17'
import { ReactComponent as GroupIcon } from '@sketch/icons/folder-open-17'
import { ReactComponent as BitmapIcon } from '@sketch/icons/image-17'
import { ReactComponent as SymbolIcon } from '@sketch/icons/symbol-17'
import { ReactComponent as GraphicIcon } from '@sketch/icons/img-frame-graphic-17'
import { ReactComponent as HStackIcon } from '@sketch/icons/stack-horizontal-17'
import { ReactComponent as VStackIcon } from '@sketch/icons/stack-vertical-17'
import { ReactComponent as HotspotIcon } from '@sketch/icons/square-prototype-17'
import { ReactComponent as CanvasDottedIcon } from '@sketch/icons/canvas-dotted-17'
import { ReactComponent as SectionIcon } from '@sketch/icons/section-17'
import { ReactComponent as VariantSetIcon } from '@sketch/icons/variants-17'

import { ExportableIcon } from '../ExportableIcon'
import { ElementType, StackDirection } from '../../../../inspector'

import * as S from './ElementIcon.styles'

export interface ElementIconProps {
  elementType: ElementType
  isLayerMasked: boolean
  isLayerExportable: boolean
  isVariantSet: boolean
  isComponent?: boolean
  isSelected?: boolean
  isStack?: boolean
  stackDirection?: StackDirection
}
export function ElementIcon({
  elementType,
  isLayerMasked,
  isLayerExportable,
  isSelected,
  isComponent,
  isVariantSet,
  isStack = false,
  stackDirection,
}: ElementIconProps) {
  const theme = useTheme()

  const isSymbolInstance = elementType === 'symbolInstance'

  // Determine mask color based on selection state and layer type
  const getMaskColor = () => {
    if (isSelected) {
      // When selected, use the selection background color
      return isSymbolInstance
        ? theme.colors.inspect.component.A
        : theme.colors.sketch.A
    }
    // Normal state uses the exportable icon theme colors
    return isComponent
      ? theme.colors.exportableIcon.symbol
      : theme.colors.exportableIcon.regular
  }

  return (
    <S.ElementIconContainer $isComponent={isComponent} $isSelected={isSelected}>
      {isLayerMasked && <S.StyledMaskedIcon />}
      <ContextMenuLayerIcon
        elementType={elementType}
        isVariantSet={isVariantSet}
        isStack={isStack}
        stackDirection={stackDirection}
      />
      {/* Slice already has an exportable icon present on the icon */}
      {isLayerExportable && (
        <ExportableIcon
          maskColor={getMaskColor()}
          isLayerMasked={isLayerMasked}
        />
      )}
    </S.ElementIconContainer>
  )
}

interface ContextMenuLayerIconProps {
  elementType: ElementType
  isVariantSet: boolean
  isStack: boolean
  stackDirection?: StackDirection
}

// Gets the element icon url based on the element type.
function ContextMenuLayerIcon({
  elementType,
  isVariantSet,
  isStack,
  stackDirection,
}: ContextMenuLayerIconProps) {
  switch (elementType) {
    case 'symbolMaster':
    case 'artboard':
      if (isStack) {
        return stackDirection === StackDirection.Vertical ? (
          <VStackIcon width={17} height={17} aria-label="Vertical stack icon" />
        ) : (
          <HStackIcon
            width={17}
            height={17}
            aria-label="Horizontal stack icon"
          />
        )
      }
      return <FrameIcon width={17} height={17} aria-label="Frame icon" />
    case 'graphic':
      return <GraphicIcon width={17} height={17} aria-label="Graphic icon" />
    case 'bitmap':
      return (
        <BitmapIcon width={17} height={17} aria-label="Bitmap layer icon" />
      )
    case 'text':
      return <TextIcon width={17} height={17} aria-label="Text layer icon" />
    case 'group':
      return <GroupIcon width={17} height={17} aria-label="Layer group icon" />
    case 'slice':
      return <CanvasDottedIcon width={17} height={17} aria-label="Slice icon" />
    case 'hotspot':
      return <HotspotIcon width={17} height={17} aria-label="Hotspot icon" />
    case 'symbolInstance':
      return <SymbolIcon width={17} height={17} aria-label="Symbol icon" />
    case 'section':
      if (isVariantSet) {
        return (
          <VariantSetIcon
            width={17}
            height={17}
            aria-label="Variant set icon"
          />
        )
      }
      return <SectionIcon width={17} height={17} aria-label="Section icon" />
    default:
      return <ShapeIcon width={17} height={17} aria-label="Shape layer icon" />
  }
}
